// Parser for the main checking CSV format. Header looks like:
//   Account Number,Post Date,Check,Description,Debit,Credit,Status,Classification
//
// Two amount columns (Debit / Credit). Debit > 0 = money out; Credit > 0 = money in.
// Our schema uses positive = money out, negative = money in.

import Papa from "papaparse";
import type { ParsedRow, ParseResult } from "./types";
import { decodeHtmlEntities, mdYyyyToIso, moneyToCents, stripBom } from "./utils";

const EXPECTED_HEADERS = [
  "Account Number",
  "Post Date",
  "Check",
  "Description",
  "Debit",
  "Credit",
  "Status",
  "Classification",
];

export function parseMainChecking(csv: string): ParseResult {
  const parsed = Papa.parse<Record<string, string>>(stripBom(csv), {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });

  if (parsed.errors.length > 0) {
    return {
      ok: false,
      error: "CSV parse error",
      details: parsed.errors.map((e) => `row ${e.row}: ${e.message}`),
    };
  }

  const headers = parsed.meta.fields ?? [];
  const missing = EXPECTED_HEADERS.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return {
      ok: false,
      error: "CSV header doesn't match the main checking format",
      details: [`Missing columns: ${missing.join(", ")}`, `Got: ${headers.join(", ")}`],
    };
  }

  const rows: ParsedRow[] = [];
  const warnings: string[] = [];

  for (let i = 0; i < parsed.data.length; i++) {
    const r = parsed.data[i];
    const lineNo = i + 2;

    const dateRaw = (r["Post Date"] || "").trim();
    const description = decodeHtmlEntities((r["Description"] || "").trim());
    const debitRaw = (r["Debit"] || "").trim();
    const creditRaw = (r["Credit"] || "").trim();
    const classification = (r["Classification"] || "").trim();

    if (!dateRaw && !description && !debitRaw && !creditRaw) continue;

    const isoDate = mdYyyyToIso(dateRaw);
    if (!isoDate) {
      warnings.push(`Line ${lineNo}: invalid date "${dateRaw}", skipping`);
      continue;
    }

    if (!description) {
      warnings.push(`Line ${lineNo}: missing description, skipping`);
      continue;
    }

    const debitCents = moneyToCents(debitRaw);
    const creditCents = moneyToCents(creditRaw);

    if (debitCents === null && creditCents === null) {
      warnings.push(`Line ${lineNo}: no amount (debit or credit), skipping`);
      continue;
    }

    let amountCents = 0;
    if (debitCents !== null && debitCents !== 0) amountCents = debitCents;
    else if (creditCents !== null && creditCents !== 0) amountCents = -creditCents;
    else {
      warnings.push(`Line ${lineNo}: zero amount, skipping`);
      continue;
    }

    rows.push({
      posted_at_iso: isoDate,
      description,
      amount_cents: amountCents,
      raw_classification: classification || undefined,
    });
  }

  return { ok: true, rows, warnings };
}
